import * as React from "react";
import { mentionToken } from "../notes/mentions";
import { userDisplay, type AppUser } from "../../services/userService";
import { useAktivSichtbar } from "./useAktivSichtbar";
import "./notizbloecke.css";

/**
 * Die Auswahl hinter dem `@` (PO 09.09.2026) — Personen erwähnen.
 *
 * Sie ist der Zwilling des {@link ./BezugPicker}: dieselbe Mechanik am Cursor,
 * dieselbe `mousedown`-Regel. Trotzdem eine eigene Komponente, weil sie etwas
 * anderes liefert — eine User-Id für `mentions`, keinen Verweis für `refs` —
 * und anderes zeigt: den Namen UND das Kürzel, das im Text stehen bleibt.
 *
 * Das Angebot kommt aus der `users`-Liste, die die Fläche ohnehin für die
 * Blockköpfe hat. Es wird nichts nachgeladen.
 */

/** Höchstzahl der Vorschläge — mehr wäre eine Nutzerliste, kein Menü. */
const MAX_TREFFER = 8;

/**
 * Wer zur Suche passt. Gesucht wird über den Anzeigenamen UND das Token, weil
 * beides im Kopf ist: „Müller" tippt, wer den Namen sucht, „mm" wer das Kürzel
 * gewohnt ist. Leere Suche = alle (man hat gerade `@` getippt).
 */
export function filterErwaehnbare(users: AppUser[], query: string): AppUser[] {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
        (u) =>
            userDisplay(u).toLowerCase().includes(q) ||
            mentionToken(u).toLowerCase().includes(q),
    );
}

/**
 * Die angebotenen Vorschläge in Anzeigereihenfolge. Das Schreibfeld braucht
 * dieselbe Liste, um Enter auf den markierten Vorschlag abzubilden (T-50).
 */
export function mentionTreffer(users: AppUser[], query: string): AppUser[] {
    return filterErwaehnbare(users, query).slice(0, MAX_TREFFER);
}

export const MentionPicker: React.FC<{
    users: AppUser[];
    query: string;
    onWaehlen: (u: AppUser) => void;
    onAbbrechen: () => void;
    /**
     * Der per Tastatur markierte Vorschlag (T-50). Die Liste selbst hat nie den
     * Fokus — das Schreibfeld fängt ↑/↓/Enter ab (`auswahlTaste`) und reicht
     * den Index herein.
     */
    aktiv?: number;
    /** Maus über einem Vorschlag — das Feld übernimmt ihn als aktiven. */
    onAktiv?: (index: number) => void;
}> = ({ users, query, onWaehlen, onAbbrechen, aktiv = 0, onAktiv }) => {
    const treffer = mentionTreffer(users, query);
    const aktivRef = useAktivSichtbar(aktiv, treffer.length);
    // Kein Treffer heißt: der Nutzer schreibt gerade eine Mailadresse oder
    // etwas anderes mit `@`. Ein leeres Menü stünde ihm dabei nur im Weg.
    if (!treffer.length) return null;

    return (
        <div
            className="nb-picker"
            role="listbox"
            tabIndex={-1}
            aria-label="Person erwähnen"
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    e.preventDefault();
                    onAbbrechen();
                }
            }}
        >
            {treffer.map((u, i) => (
                <button
                    key={u.id}
                    ref={i === aktiv ? aktivRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={i === aktiv}
                    className={i === aktiv ? "nb-picker-item ist-aktiv" : "nb-picker-item"}
                    onMouseEnter={onAktiv ? () => onAktiv(i) : undefined}
                    onMouseDown={(e) => {
                        // mousedown statt click: ein Klick nähme dem Feld
                        // zuerst den Fokus, und die Cursorposition, an der die
                        // Erwähnung landen soll, wäre verloren.
                        e.preventDefault();
                        onWaehlen(u);
                    }}
                >
                    <span className="nb-picker-art nb-art-person">@{mentionToken(u)}</span>
                    <span className="nb-picker-titel">{userDisplay(u)}</span>
                </button>
            ))}
        </div>
    );
};
